import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { SearchQuery } from '@modules/feed/types';
import { deserializeDuration } from '@modules/feed/utils/deserializeDuration';
import { deserializeWhereClause } from '@modules/feed/utils/deserializeWhereClause';
import { getDurationFromAssistantResponseEqlQuery } from '@modules/oracle/utils';
import { EqlQueryProperties } from 'generated/graphql';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseExpression } from 'v2/core/repository/eql/parser';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface GetSearchQueryFromMetadataOptions {
  metadata?: string;
  getDurationField: boolean;
  defaultTimeRange: DateTimeRange;
  eqlProperties: EqlQueryProperties | undefined;
  sourceDetailsMap:
    | ReturnType<typeof useGetSourceDetailsMap>['data']
    | undefined;
}

export const getSearchQueryFromMetadata = ({
  metadata,
  getDurationField,
  defaultTimeRange,
  eqlProperties,
  sourceDetailsMap,
}: GetSearchQueryFromMetadataOptions): Partial<SearchQuery> => {
  const getSearchQuery = () => {
    try {
      const dashboardURL = new URL(metadata || window.location.href);
      const search = new URLSearchParams(dashboardURL.search);

      const searchDuration = decodeURIComponent(search.get('duration') ?? '');

      // There can be cases wherein whereClause has a '%' in it. When that happens,
      // decodeURIComponent throws a error since it always decodes '%' that are followed
      // by two hexadecimal digits which represent a encoded character.
      // For fix we are replacing it with '%25' which decodes to just '%'.
      const searchWhereClause = decodeURIComponent(
        search.get('where')?.replace(/%/g, '%25') ?? '',
      );

      const duration = searchDuration
        ? deserializeDuration(searchDuration)
        : defaultTimeRange;

      const whereClause = searchWhereClause
        ? deserializeWhereClause(searchWhereClause)
        : EmptyWhereClause;

      return {
        duration,
        whereClause,
      };
    } catch {
      // Fallback to eqlProperties if metadata parsing fails
      return {
        duration: getDurationFromAssistantResponseEqlQuery(
          eqlProperties?.duration,
        ),
        whereClause:
          sourceDetailsMap && eqlProperties?.whereClause
            ? parseExpression(eqlProperties.whereClause, sourceDetailsMap)
            : undefined,
      };
    }
  };

  const { duration, whereClause } = getSearchQuery();

  if (getDurationField) {
    return {
      duration,
      whereClause,
    };
  }

  return {
    whereClause,
  };
};
